import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Activity, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const { login, status } = useAuth();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (status === "authed") {
    const to = location.state?.from?.pathname || "/status";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await login(password);
      toast.success("Logged in");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-4">
      <div className="w-full max-w-[420px]">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 brand-bg rounded-sm flex items-center justify-center">
            <Activity className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">Namecheap</div>
            <div className="font-semibold tracking-tight text-[15px]">Control Panel</div>
          </div>
        </div>

        <Card className="rounded-sm shadow-none border-[var(--border)]">
          <CardContent className="p-8 space-y-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                Restricted access
              </div>
              <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                Enter the dashboard password to continue.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider">
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-disabled)]" />
                  <Input
                    data-testid="login-password"
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••••••"
                    autoFocus
                    className="mono rounded-sm pl-9"
                  />
                </div>
              </div>

              <Button
                data-testid="login-submit"
                type="submit"
                disabled={busy || !password}
                className="w-full brand-bg hover:brand-bg text-white rounded-sm h-10"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {busy ? "Verifying..." : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-xs text-[var(--text-disabled)] mt-4 text-center mono">
          v1 · single-password gate · no cookies
        </p>
      </div>
    </div>
  );
};

export default Login;
